import { PhoneIcon, Trash2Icon } from "lucide-react";
import { useId, useState } from "react";
import { type Control, type FieldPath, useController } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import { FormMessage } from "../../../../../components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { Input } from "../../../../../components/ui/uikit/input";
import { useCheckForceExit } from "../../../../../hooks/useCheckForceExit";
import ForceExitWarningModal from "../../../../exit/ForceExitWarningModal";
import { PHONE_EXTENSION_MAX_LENGTH, PHONE_TYPE_OPTIONS } from "../constants";
import { useFormReadOnly } from "../readOnlyContext";
import type { SignersInput } from "../Signers.schema";
import { buildTelHref } from "./buildTelHref";
import { PhoneNumberInput } from "./PhoneNumberInput";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type PersonPath = "driver" | "secondSigner";

interface PhoneRowProperties {
  control: Control<SignersInput>;
  personPath: PersonPath;
  index: number;
  /** Called when the user clicks remove. Undefined for the Primary (index 0) and Secondary (index 1) rows — those are always present by default. */
  onRemove?: () => void;
  /**
   * Renders the residence "Call Script" button beside the call icon. Set by
   * CustomerInformation for the Primary row only — the customer-verification
   * script is per-person (CAPS2's single `step6DriverCallApplicantButton` on
   * the phone+email panel), not per phone number.
   */
  showCallScript?: boolean;
  /**
   * Opens the residence Call Script. The modal is owned by the signer column,
   * not this row: a form `reset()` regenerates field-array ids and remounts
   * this row, which would drop any modal state held here.
   */
  onCallScriptOpen?: () => void;
}

// ---------------------------------------------------------------------------
// PhoneRow
//
// A single phone entry: single auto-formatted phone number input + type select
// (Personal/Work) + optional extension (Work type only) + remove button for
// non-primary rows.
//
// Primary row (index 0): type locked to "Personal" (disabled select), not removable.
// Secondary row (index 1): full Personal/Work select, not removable (present by default).
// Rows beyond Secondary (index > 1): full Personal/Work select + removable.
// Extension input shown only when type === "Work".
// ---------------------------------------------------------------------------
export function PhoneRow({
  control,
  personPath,
  index,
  onRemove,
  showCallScript = false,
  onCallScriptOpen,
}: PhoneRowProperties) {
  const { t } = useTranslation();
  const uid = useId();
  const isPrimary = index === 0;
  // The call icon (<a>) and the Radix type Select both ignore a disabled
  // <fieldset>, so under the VLA lock we disable them from the shared context.
  const readOnly = useFormReadOnly();
  const [forceExitWarningOpen, setForceExitWarningOpen] = useState(false);
  const { canProceed: canOpenCallScript } = useCheckForceExit();

  const handleCallScriptClick = () => {
    if (canOpenCallScript) {
      onCallScriptOpen?.();
    } else {
      setForceExitWarningOpen(true);
    }
  };

  const numberPath = `${personPath}.customerInfo.phones.${index}.number` as FieldPath<SignersInput>;
  const typePath = `${personPath}.customerInfo.phones.${index}.type` as FieldPath<SignersInput>;
  const extensionPath = `${personPath}.customerInfo.phones.${index}.extension` as FieldPath<SignersInput>;

  const { field: numberField, fieldState: numberFieldState } = useController({ control, name: numberPath });
  const { field: typeField } = useController({ control, name: typePath });
  const { field: extensionField, fieldState: extensionFieldState } = useController({ control, name: extensionPath });

  const typeSelectId = `${uid}-phone-type`;
  const extensionInputId = `${uid}-phone-extension`;

  // Derive the visible row label — used for the <label> element and as the
  // ariaLabel for the phone number input.
  let rowLabel: string;
  if (isPrimary) {
    rowLabel = t("review.customer.contact.primaryPhone");
  } else if (index === 1) {
    rowLabel = t("review.customer.contact.secondaryPhone");
  } else {
    rowLabel = t("review.customer.contact.phone");
  }

  // For index >= 2, multiple rows share the label "Phone". Append the position
  // (1-based) so each row's aria-label remains unique (e.g. "Phone 2").
  const ariaLabelBase = index >= 2 ? `${rowLabel} ${index}` : rowLabel;

  const typeValue = typeField.value == null ? "Personal" : String(typeField.value);
  const isWork = typeValue === "Work";

  // Canonical digits for the tel: link (the call icon dials the entered number,
  // plus the extension — as a DTMF pause — when this is a Work-type row).
  const numberDigits = (numberField.value == null ? "" : String(numberField.value)).replaceAll(/\D/g, "");
  const extensionDigits = (extensionField.value == null ? "" : String(extensionField.value)).replaceAll(/\D/g, "");

  function handleTypeChange(newType: string) {
    typeField.onChange(newType);
    // Clear extension when switching away from Work
    if (newType !== "Work") {
      extensionField.onChange("");
    }
  }

  return (
    <>
      <div className="@sm:flex-row @sm:items-center @sm:gap-3 flex flex-col gap-1">
        {/* Label — fixed-width column at @sm+ so all rows align */}
        <label className="@sm:w-[var(--signer-label-width)] @sm:shrink-0 text-xs font-medium">
          {rowLabel}
          {isPrimary && (
            <span className="text-destructive ml-0.5" aria-hidden="true">
              *
            </span>
          )}
        </label>

        {/* Inline controls + inline errors below them */}
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-1">
            {/* Phone number input */}
            <PhoneNumberInput
              idBase={`${uid}-phone-number`}
              value={numberField.value == null ? "" : String(numberField.value)}
              onChange={numberField.onChange}
              onBlur={numberField.onBlur}
              ariaLabel={ariaLabelBase}
              required={isPrimary}
              invalid={Boolean(numberFieldState.error)}
            />

            {/* Phone type select — aria-labelled only (no visible sub-label) */}
            <Select value={typeValue} onValueChange={handleTypeChange} disabled={isPrimary || readOnly}>
              <SelectTrigger
                id={typeSelectId}
                className="w-28"
                aria-label={t("review.customer.contact.phoneType")}
                disabled={isPrimary || readOnly}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PHONE_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Extension — only visible when type === "Work"; aria-labelled only */}
            {isWork && (
              <Input
                id={extensionInputId}
                type="text"
                inputMode="numeric"
                value={extensionField.value == null ? "" : String(extensionField.value)}
                onChange={(event) => {
                  extensionField.onChange(
                    event.target.value.replaceAll(/\D/g, "").slice(0, PHONE_EXTENSION_MAX_LENGTH)
                  );
                }}
                onBlur={extensionField.onBlur}
                ref={extensionField.ref}
                maxLength={PHONE_EXTENSION_MAX_LENGTH}
                placeholder={t("review.customer.contact.extension")}
                aria-label={t("review.customer.contact.extension")}
                aria-invalid={Boolean(extensionFieldState.error) || undefined}
                className="w-16"
              />
            )}

            {/* Call icon — dials the entered number via tel: when a number is
              present and the deal is editable. Under the VLA lock (read-only),
              or when no number is entered yet, the same slot shows a muted,
              inert icon (an <a> can't be disabled by the enclosing fieldset)
              instead of announcing a dead link to assistive technology. */}
            {numberDigits && !readOnly ? (
              <a
                href={buildTelHref(numberDigits, extensionDigits)}
                aria-label={t("review.customer.contact.call")}
                className="text-success-foreground border-success-foreground hover:bg-success flex size-6 shrink-0 items-center justify-center rounded-md border transition-colors"
              >
                <PhoneIcon className="size-3.5 fill-current" aria-hidden="true" />
              </a>
            ) : (
              <span
                aria-hidden="true"
                className="text-muted-foreground border-muted flex size-6 shrink-0 items-center justify-center rounded-md border opacity-60"
              >
                <PhoneIcon className="size-3.5 fill-current" aria-hidden="true" />
              </span>
            )}

            {/* Call Script — opens the customer-verification script for this
              person. Force-exit is checked before opening. Not rendered at all
              while the section is read-only (CAPS2 showCallScriptButton). */}
            {showCallScript && !readOnly && (
              <button
                type="button"
                onClick={handleCallScriptClick}
                className="bg-primary text-primary-foreground border-primary hover:bg-primary/90 flex h-6 shrink-0 items-center justify-center whitespace-nowrap rounded-md border px-3 text-xs font-semibold transition-colors"
              >
                {t("review.customer.contact.callScript")}
              </button>
            )}

            {/* Remove button — non-primary rows only. */}
            {!isPrimary && onRemove && (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={onRemove}
                aria-label={t("review.customer.contact.removePhone", { position: index + 1 })}
                className="text-destructive hover:text-destructive shrink-0"
              >
                <Trash2Icon className="size-4" aria-hidden="true" />
              </Button>
            )}
          </div>

          <FormMessage error={numberFieldState.error} className="mt-0" />
          <FormMessage error={extensionFieldState.error} className="mt-0" />
        </div>
      </div>

      {showCallScript && (
        <ForceExitWarningModal
          open={forceExitWarningOpen}
          onOpenChange={setForceExitWarningOpen}
          onConfirm={() => setForceExitWarningOpen(false)}
        />
      )}
    </>
  );
}
